import { ScreenMetadata, UISchema, ActionDefinition, EvaluationContext } from '@types/index';
import { componentRegistry, schemaValidator } from './registry';

interface ScreenRenderOptions {
  validateSchema?: boolean;
  context?: EvaluationContext;
}

export class ScreenManager {
  private screens: Map<string, ScreenMetadata> = new Map();
  private currentScreen: ScreenMetadata | null = null;

  registerScreen(screen: ScreenMetadata): boolean {
    if (!screen.id || !screen.name) {
      console.error('Screen must have id and name');
      return false;
    }

    const validation = schemaValidator.validate(screen.schema);
    if (!validation.valid) {
      console.error('Invalid screen schema:', validation.errors);
      return false;
    }

    this.screens.set(screen.id, screen);
    return true;
  }

  getScreen(screenId: string): ScreenMetadata | undefined {
    return this.screens.get(screenId);
  }

  getScreens(): ScreenMetadata[] {
    return Array.from(this.screens.values());
  }

  setCurrentScreen(screenId: string): boolean {
    const screen = this.screens.get(screenId);
    if (!screen) {
      console.error(`Screen "${screenId}" not found`);
      return false;
    }
    this.currentScreen = screen;
    return true;
  }

  getCurrentScreen(): ScreenMetadata | null {
    return this.currentScreen;
  }

  executeAction(
    actionId: string,
    context?: EvaluationContext,
    options?: {
      onNavigate?: (screenId: string) => void;
      onAPICall?: (endpoint: string, method: string, data?: any) => Promise<any>;
      onEvent?: (eventName: string, payload?: any) => void;
    }
  ): void {
    if (!this.currentScreen) {
      console.error('No current screen set');
      return;
    }

    const action = this.currentScreen.actions?.find((a) => a.id === actionId);
    if (!action) {
      console.error(`Action "${actionId}" not found in current screen`);
      return;
    }

    switch (action.type) {
      case 'navigate':
        if (action.target && options?.onNavigate) {
          options.onNavigate(action.target);
        }
        break;

      case 'api':
        if (options?.onAPICall) {
          const { target, payload } = action;
          if (target) {
            options.onAPICall(target, payload?.method || 'POST', payload?.data);
          }
        }
        break;

      case 'event':
        if (options?.onEvent) {
          options.onEvent(action.target || actionId, action.payload);
        }
        break;

      case 'callback':
        if (action.handler) {
          action.handler(context);
        }
        break;

      default:
        console.warn(`Unknown action type: ${action.type}`);
    }
  }

  validateScreenComponents(): boolean {
    for (const screen of this.screens.values()) {
      const validation = schemaValidator.validate(screen.schema);
      if (!validation.valid) {
        console.error(`Screen "${screen.id}" has invalid schema:`, validation.errors);
        return false;
      }

      if (!this.validateSchemaComponents(screen.schema)) {
        return false;
      }
    }
    return true;
  }

  private validateSchemaComponents(schema: UISchema): boolean {
    const componentType = schema.type.toLowerCase();
    if (!componentRegistry.has(componentType)) {
      console.error(`Component type "${schema.type}" is not registered`);
      return false;
    }

    if (schema.children) {
      return schema.children.every((child) => this.validateSchemaComponents(child));
    }

    return true;
  }

  clear(): void {
    this.screens.clear();
    this.currentScreen = null;
  }
}

export const screenManager = new ScreenManager();
